import { useCallback, useMemo, useRef } from 'react';
import uniq from 'lodash/uniq';

import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import { PStyled } from 'approot/shared/styles/typography.style';
import { grey7 } from 'approot/shared/styles/colours.style';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { SecondaryButtonIcon } from 'approot/shared/styles/buttons/secondary-button.style';
import ImageThumbnail from 'approot/shared/images/images';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import { useBatchComponentUpdateContext } from '../../shared/batch-component-update.context';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useVocabularySettingContext } from './vocabulary-setting.context';
import {
  DictionaryDefinition,
  VOCABULARY_SETTINGS_ASK_AI_FOR_NEW_DEFINITION,
} from './vocabulary-setting.constants';
import { getLanguageLabel, getVocabularyIds } from './vocabulary-setting.utils';
import VocabularySearch from './vocabulary-search';
import * as Styled from './vocabulary-setting-words.style';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import {
  DeleteWordConfirm,
  VocabularyApprovedTooltip,
} from './vocabulary-setting-shared';
import { useRemoveDefinition } from './vocabulary-setting.hooks';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { BadgeSmallStyled } from 'approot/shared/badge/badge.style';
import IconTrash from 'approot/shared/icons/icon-trash';

const VocabularySettingWords = ({
  componentId,
  definitions,
  goBack,
  goTo,
  editApprovedDefinition,
  editUnapprovedDefinition,
}: {
  componentId: string;
  definitions: DictionaryDefinition[];
  goBack: () => void;
  goTo: (id: number) => void;
  editApprovedDefinition: (definition: DictionaryDefinition) => void;
  editUnapprovedDefinition: (suggestion: DictionaryDefinition) => void;
}) => {
  const removeRef = useRef<DictionaryDefinition | null>(null);

  const { sectionId, word, yearGroup, contentLabel, setWord } =
    useVocabularySettingContext();

  const {
    isVisible: isConfirmDeleteVisible,
    openModal: openConfirmDeleteModal,
    closeModal: closeConfirmDeleteModal,
  } = useSimpleModalState();

  const { showError, showSuccess } = useSnackbar();
  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const { loading: loadingRemoveFromComponent, handleRemoveFromComponent } =
    useRemoveDefinition({
      componentId,
      sectionId,
      definitions,
      onCompleted: closeConfirmDeleteModal,
    });

  const vocabularyIds = useMemo(() => {
    return getVocabularyIds(definitions);
  }, [definitions]);

  const handleAddToContent = useCallback(
    (id: number) => {
      updateComponentProperties({
        input: {
          componentId,
          sectionId,
          componentData: {
            properties: {
              definitionIds: [...vocabularyIds, id],
            },
          },
        },
        properties: {
          __typename: 'VocabularyComponentProperties',
          definitionIds: [
            ...vocabularyIds.map(id => id.toString()),
            id.toString(),
          ],
          style: null,
        },
        typename: 'ActivitiesVocabularyComponent',
        isSaveImmediate: true,
        onCompleted: () => {
          setWord('');
          showSuccess('Vocabulary updated');
        },
        onError: () => showError('There was an error saving the vocabulary'),
      });
    },
    [
      componentId,
      sectionId,
      vocabularyIds,
      setWord,
      showSuccess,
      showError,
      updateComponentProperties,
    ]
  );

  return (
    <ActivityBuilderDrawerWrapper>
      {isConfirmDeleteVisible && removeRef.current && (
        <DeleteWordConfirm
          word={removeRef.current.word}
          isLoading={loadingRemoveFromComponent}
          onClose={closeConfirmDeleteModal}
          onConfirm={() => {
            handleRemoveFromComponent(removeRef.current?.id!);
          }}
        />
      )}
      <ActivityBuilderDrawerHeader title="Vocabulary" onClose={goBack} />
      <ActivityBuilderDrawerContent>
        <VocabularySearch
          fieldName="search"
          term={word}
          vocabularyIds={vocabularyIds}
          yearGroup={yearGroup}
          contentLabel={contentLabel}
          setTerm={setWord}
          addDefinitionToContent={handleAddToContent}
          askAIForRawDefinition={() =>
            goTo(VOCABULARY_SETTINGS_ASK_AI_FOR_NEW_DEFINITION)
          }
          editUnapprovedDefinition={editUnapprovedDefinition}
        />

        {definitions.length > 0 && (
          <>
            <Box $marginBottom={16} data-selector="added-vocabulary">
              <BoxList>
                {definitions.map(item => {
                  const languages = uniq(
                    item.aiDefinitions?.definitions.map(def => def.language)
                  );
                  const description = item.aiDefinitions?.definitions.find(
                    d => d.year === yearGroup
                  );
                  const exampleImage = item.aiDefinitions?.examples.find(
                    ex => ex.image
                  )?.image;

                  return (
                    <Styled.VocabularyListItem
                      key={item.id}
                      data-selector="added-vocab-list-item"
                    >
                      <Styled.VocabularyListButtonTrigger
                        type="button"
                        onClick={() => editApprovedDefinition(item)}
                      >
                        <ScreenReaderOnly data-selector="vocab-details">
                          {item.word}, {item.lexical}
                        </ScreenReaderOnly>
                      </Styled.VocabularyListButtonTrigger>
                      <Styled.VocabularyListContent>
                        <Styled.VocabularyListDetails>
                          <Box $marginRight={8}>
                            {exampleImage ? (
                              <ImageThumbnail
                                width={40}
                                images={exampleImage.responsive}
                              />
                            ) : (
                              <Styled.VocabularyImagePicture>
                                <InlineSvg id="image" width={24} height={24} />
                              </Styled.VocabularyImagePicture>
                            )}
                          </Box>
                          <div aria-hidden>
                            <Styled.VocabularyListData data-selector="vocab-list-data">
                              <strong>{item.word}</strong>{' '}
                              <i>({item.lexical})</i>
                              <VocabularyApprovedTooltip />
                            </Styled.VocabularyListData>
                            <PStyled colour={grey7} marginBottom={0}>
                              {description?.definition}
                            </PStyled>
                            {languages.length > 1 && (
                              <Styled.VocabularyLanguage>
                                {languages.map(l => (
                                  <BadgeSmallStyled key={l}>
                                    {getLanguageLabel(l)}
                                  </BadgeSmallStyled>
                                ))}
                              </Styled.VocabularyLanguage>
                            )}
                          </div>
                        </Styled.VocabularyListDetails>
                        <Styled.VocabularyListButtonAction>
                          {item.id && (
                            <TooltipWrapper
                              hoverText={`Remove from ${contentLabel}`}
                            >
                              <SecondaryButtonIcon
                                type="button"
                                aria-label={`Remove ${item.word}`}
                                $isSmall
                                onClick={() => {
                                  removeRef.current = item;
                                  openConfirmDeleteModal();
                                }}
                                data-selector="btn-remove"
                              >
                                <IconTrash width={20} height={20} />
                              </SecondaryButtonIcon>
                            </TooltipWrapper>
                          )}
                        </Styled.VocabularyListButtonAction>
                      </Styled.VocabularyListContent>
                    </Styled.VocabularyListItem>
                  );
                })}
              </BoxList>
            </Box>
          </>
        )}
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default VocabularySettingWords;
